import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Switch,
    FormControlLabel,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { displayMarketplaceValue } from './marketplaceDiff';
// Reuse the Statement of Taxes audit styling so both previews share the exact same table look.
import '../../statementTaxes/preview/StatementTaxesAuditPreviewDialog.css';
import './MarketplaceAuditPreviewDialog.css';

const PRIMARY = '#005E73';

class MarketplaceAuditPreviewDialog extends React.Component {
    state = {
        showUnchanged: false,
    };

    toggleUnchanged = () => this.setState(prev => ({ showUnchanged: !prev.showUnchanged }));

    // Zero / empty values are blank in the Marketplace Summary table, so blank them here too —
    // except inside an actual change (e.g. 0.00 -> 100), where the zero is meaningful.
    isZeroOrBlank = (value) => {
        if (value === null || value === undefined || value === '') return true;
        const stripped = String(value).replace(/[(),]/g, '').trim();
        if (stripped === '') return true;
        const n = parseFloat(stripped);
        return !isNaN(n) && n === 0;
    };

    // A single "$            1,234.56" line — dollar pinned left, amount pinned right, exactly like
    // the Marketplace Summary table cells. blankZero hides 0/0.00 to mirror the table.
    renderMoneyLine = (value, variant, blankZero = true) => {
        const text = (blankZero && this.isZeroOrBlank(value)) ? '' : displayMarketplaceValue(value);
        return (
            <div className={`sta-money sta-money-${variant}`}>
                <span className="sta-dollar">{text === '' ? '' : '$'}</span>
                <span className="sta-amount">{text === '' ? ' ' : text}</span>
            </div>
        );
    };

    // Numeric cell. Changed cells stack the old value on the first line and the new value below it —
    // zeros are shown there because they are part of the change.
    renderNumericCell = (field, status, key) => {
        if (status === 'removed') {
            return <td key={key} className="sta-cell sta-cell-num">{this.renderMoneyLine(field.old, 'removed')}</td>;
        }
        if (status === 'added') {
            return <td key={key} className="sta-cell sta-cell-num">{this.renderMoneyLine(field.new, 'added')}</td>;
        }
        if (field.changed) {
            return (
                <td key={key} className="sta-cell sta-cell-num">
                    {this.renderMoneyLine(field.old, 'old', false)}
                    {this.renderMoneyLine(field.new, 'new', false)}
                </td>
            );
        }
        const val = field.new !== undefined ? field.new : field.old;
        return <td key={key} className="sta-cell sta-cell-num">{this.renderMoneyLine(val, 'plain')}</td>;
    };

    renderNameCell = (row, key) => {
        const name = (row.fields.name && (row.fields.name.new !== undefined ? row.fields.name.new : row.fields.name.old)) || row.name;
        return (
            <td key={key} className="sta-cell sta-cell-cat">
                <span className={`sta-cat sta-cat-${row.status}`}>{name}</span>
            </td>
        );
    };

    renderSection = (section) => {
        const { showUnchanged } = this.state;
        const visibleRows = section.rows.filter(r => showUnchanged || r.status !== 'unchanged');
        const idField = section.idField || 'name';

        return (
            <div className="mdp-table-wrap" key={section.key}>
                {visibleRows.length === 0 ? (
                    <div className="sta-section-empty">No changes in this summary.</div>
                ) : (
                    <table className="sta-table mdp-table">
                        <thead>
                            <tr>
                                {section.cellConfig.map((cell) => (
                                    <th
                                        key={cell.id}
                                        className={`sta-th ${cell.id === idField ? 'sta-th-left' : 'sta-th-right'}`}
                                    >
                                        {cell.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((row, idx) => (
                                <tr key={`${section.key}-${row.id}-${idx}`} className={`sta-row sta-row-${row.status}${row.bold ? ' sta-row-bold' : ''}`}>
                                    {section.cellConfig.map((cell) => (
                                        cell.id === idField
                                            ? this.renderNameCell(row, cell.id)
                                            : this.renderNumericCell(row.fields[cell.id] || {}, row.status, cell.id)
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        );
    };

    render() {
        const { open, loading, diff, onApply, onCancel } = this.props;
        const counts = (diff && diff.counts) || { changed: 0, added: 0, removed: 0 };
        const totalChanges = counts.changed + counts.added + counts.removed;
        const sections = (diff && diff.sections) || [];
        const noChanges = !loading && totalChanges === 0;

        return (
            <Dialog
                open={open}
                onClose={onCancel}
                maxWidth="lg"
                fullWidth
                classes={{ paper: 'sta-paper' }}
            >
                <div className="sta-header">
                    <div className="sta-header-main">
                        <div className="sta-title-block">
                            <span className="sta-eyebrow">Refresh</span>
                            <span className="sta-title">Marketplace Summary</span>
                        </div>
                        <IconButton onClick={onCancel} className="sta-close" size="small">
                            <CloseIcon fontSize="small" htmlColor="#ffffff" />
                        </IconButton>
                    </div>
                    <div className="sta-summary">
                        <span className="sta-chip sta-chip-changed">{counts.changed} Changed</span>
                        <span className="sta-chip sta-chip-added">{counts.added} New</span>
                        <span className="sta-chip sta-chip-removed">{counts.removed} Removed</span>
                    </div>
                </div>

                {!loading && !noChanges && (
                    <div className="sta-subbar">
                        <div className="sta-legend">
                            <span className="sta-legend-item"><i className="sta-dot sta-dot-changed" />Changed value</span>
                            <span className="sta-legend-item"><i className="sta-dot sta-dot-added" />New row</span>
                            <span className="sta-legend-item"><i className="sta-dot sta-dot-removed" />Removed row</span>
                        </div>
                        <FormControlLabel
                            className="sta-toggle"
                            control={
                                <Switch
                                    size="small"
                                    checked={this.state.showUnchanged}
                                    onChange={this.toggleUnchanged}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
                                    }}
                                />
                            }
                            label="Show unchanged"
                        />
                    </div>
                )}

                <DialogContent className="sta-content" dividers>
                    {loading ? (
                        <div className="sta-loading">
                            <CircularProgress style={{ color: PRIMARY }} />
                            <span className="sta-loading-text">Calculating changes…</span>
                        </div>
                    ) : noChanges ? (
                        <div className="sta-nochanges">
                            <CheckCircleOutlineIcon style={{ color: PRIMARY, fontSize: 48 }} />
                            <span className="sta-nochanges-title">No changes detected</span>
                            <span className="sta-nochanges-sub">Refreshing now would not modify this summary.</span>
                        </div>
                    ) : (
                        sections.map(this.renderSection)
                    )}
                </DialogContent>

                <DialogActions className="sta-actions">
                    <div className="sta-actions-buttons">
                        <Button onClick={onCancel} variant="outlined" className="sta-btn-cancel">
                            Cancel
                        </Button>
                        <Tooltip title={noChanges ? 'Nothing to apply' : ''} disableHoverListener={!noChanges}>
                            <span>
                                <Button
                                    onClick={onApply}
                                    variant="contained"
                                    disableElevation
                                    disabled={loading || noChanges}
                                    className="sta-btn-apply"
                                >
                                    Apply Changes
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                </DialogActions>
            </Dialog>
        );
    }
}

export default MarketplaceAuditPreviewDialog;
